/**
 * useAnalyzeReel Hook
 * 
 * Instagram Reel analizi için tüm state ve logic'i yöneten custom hook.
 * HomePage.js'den ayrıştırıldı - tek sorumluluk prensibi.
 * 
 * OPARA v12.0: Global Analysis Tracker entegrasyonu
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useAnalysisTracker } from '@/contexts/AnalysisTrackerContext';
import { getReadableErrorMessage } from '@/utils/errorHandler';
import { isPersonaStaff } from '@/utils/staff';
import useTranslation from '@/hooks/useTranslation';

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL, API } from '@/utils/apiConfig';

// v14.2: Placeholder mesajlar kaldırıldı - Progress bar artık tek gerçek kaynak

// v15.0: LocalStorage key for persistent report
const PERSISTENT_RESULT_KEY = 'opara_last_analysis_result';

/**
 * mapV4Result - Backend v4 sonucunu frontend formatına dönüştürür
 */
const mapV4Result = (v4Result, usernameInput, t = null) => {
  // ================================================================
  // OPARA v18.4 - BACKWARD COMPATIBLE MAPPER
  // Supports both OLD (Turkish) and NEW (English) field names
  // ================================================================
  
  // Helper: Get value from either new or old field name
  const getField = (newKey, oldKey, obj) => obj?.[newKey] ?? obj?.[oldKey];
  
  // Core metrics - support both naming conventions
  const coreMetrics = v4Result.opara_core_metrikleri || {};
  const otherMetrics = v4Result.other_metrics || v4Result.diger_metrikler || {};
  const validation = v4Result.validation_status || v4Result.guvenlik_kontrolleri_durumu || {};
  const userInputs = v4Result.user_inputs || v4Result.manuel_girisler || {};
  
  // Basket/Sepet counts for validation
  const basket1Count = getField('basket_1_count', 'sepet_1_reel_sayisi', validation) || 0;
  const basket2Count = getField('basket_2_count', 'sepet_2_reel_sayisi', validation) || 0;
  
  // v16.5 HANDBOOK: SEPET-1 = 0 → "Hesaplanamadı, son 90 gün içinde reel paylaşmamış"
  const basket1ZeroError = basket1Count === 0 ? (t ? t('results.basket1_zero') : "Hesaplanamadı, son 90 gün içinde reel paylaşmamış") : null;
  // v31: KALDIRILDI — "SEPET-2 < 3 → Like'lar Gizli" turetmesi handbook 4.3'e AYKIRIYDI
  // (veri=2 gecerli deger uretir). Artik backend'in OTORITER bayraklari okunuyor.
  const backendAllLikesHidden = coreMetrics.all_likes_hidden === true;          // GERCEK gizli-like
  const backendInsufficient = (getField('insufficient_data', 'yetersiz_veri', validation) === true)
    || (getField('audit_status', 'audit_durumu', v4Result) === 'INSUFFICIENT_DATA')
    || basket1Count === 0;                                                       // GERCEK yetersiz veri
  const insufficientError = backendInsufficient ? (t ? t('results.insufficient_data') : 'Yetersiz Veri') : null;
  
  // Follower count parsing
  const followerCountRaw = getField('follower_count', 'takipci_sayisi', userInputs);
  const followerCount = typeof followerCountRaw === 'string' 
    ? parseInt(followerCountRaw.replace(/,/g, ''))
    : (followerCountRaw || 0);
  
  // Payment per reel parsing
  const paymentRaw = getField('payment_per_reel', 'reel_is_birligi_maliyeti_tl', userInputs);
  const paymentPerReel = paymentRaw !== "N/A" 
    ? (typeof paymentRaw === 'string' ? parseInt(paymentRaw.replace(/,/g, '')) : paymentRaw)
    : null;
  
  return {
    // Basic info
    username: v4Result.username || usernameInput,
    profile_pic_url: v4Result.profile_pic_url || v4Result.profile?.profile_pic_url,
    follower_count: followerCount,
    payment_per_reel: paymentPerReel,
    // C1: persist edilen secili para birimi (user_inputs.currency) -> AnalysisResults etiketi
    currency: getField('currency', 'currency', userInputs) || null,

    // Core metrics - Expected Views
    target_reel_views: (() => {
      const val = getField('expected_views_avg', 'beklenen_reel_izlenme_adet', coreMetrics);
      if (!val || val === "N/A" || val === "-") return null;
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const cleaned = val.replace(/,/g, '').trim();
        if (cleaned.endsWith('K')) return parseFloat(cleaned.replace('K', '')) * 1000;
        if (cleaned.endsWith('M')) return parseFloat(cleaned.replace('M', '')) * 1000000;
        return parseFloat(cleaned);
      }
      return null;
    })(),
    target_reel_views_range_min: getField('expected_views_min', 'beklenen_izlenme_araligi_min', coreMetrics) || null,
    target_reel_views_range_max: getField('expected_views_max', 'beklenen_izlenme_araligi_max', coreMetrics) || null,
    target_reel_views_range_display: getField('expected_views_display', 'beklenen_izlenme_araligi_display', coreMetrics) || '-',
    target_reel_views_error: basket1ZeroError,
    
    // Story Views
    story_view_range: getField('story_views_display', 'beklenen_story_izlenme_araligi', coreMetrics) || null,
    story_view_min: getField('story_views_min', 'beklenen_story_izlenme_min', coreMetrics) || null,
    story_view_max: getField('story_views_max', 'beklenen_story_izlenme_max', coreMetrics) || null,
    story_view_average: getField('story_views_avg', 'beklenen_story_izlenme_mid', coreMetrics) || null,
    
    // Link Clicks
    link_click_range: getField('link_clicks_display', 'beklenen_link_tiklama_araligi', coreMetrics) || null,
    link_click_min: getField('link_clicks_min', 'beklenen_link_tiklama_min', coreMetrics) || null,
    link_click_max: getField('link_clicks_max', 'beklenen_link_tiklama_max', coreMetrics) || null,
    
    // Cost Per View
    cost_per_view: (() => {
      const val = getField('cpv', 'izlenme_basina_maliyet_tl', coreMetrics);
      return val !== "N/A" && val !== "-" ? parseFloat(val) : null;
    })(),
    
    // Expected Engagement
    total_engagement: (() => {
      const val = getField('expected_engagement_avg', 'beklenen_is_birligi_etkilesimi_adet', coreMetrics);
      return typeof val === 'string' ? parseInt(val.replace(/,/g, '')) : (val || 0);
    })(),
    engagement_range_min: getField('expected_engagement_min', 'beklenen_etkilesim_araligi_min', coreMetrics) || null,
    engagement_range_max: getField('expected_engagement_max', 'beklenen_etkilesim_araligi_max', coreMetrics) || null,
    engagement_range_display: getField('expected_engagement_display', 'beklenen_etkilesim_araligi_display', coreMetrics) || '-',
    total_engagement_error: insufficientError,
    
    // Interaction Rate
    expected_interaction_rate: (() => {
      const val = getField('expected_er_percent', 'beklenen_etkilesim_orani_yuzde', coreMetrics);
      return typeof val === 'string' ? parseFloat(val.replace('%', '')) : (val || 0);
    })(),
    
    // Engagement Per Reel
    follower_interaction: getField('engagement_per_reel', 'reel_basina_etkilesim_adet', coreMetrics) || 0,
    follower_interaction_error: insufficientError,
    
    // Loyalty Rate
    engagement_rate: (() => {
      const val = getField('loyalty_rate_percent', 'takipci_baglilik_orani_yuzde', coreMetrics);
      return typeof val === 'string' ? parseFloat(val.replace('%', '')) : parseFloat(val || 0);
    })(),
    engagement_evaluation: getField('loyalty_rating', 'baglilik_oranı_derecelendirme', coreMetrics) || '-',
    engagement_segment_description: coreMetrics.segment_description || '',
    visibility_segment_description: coreMetrics.visibility_segment_description || '',
    
    // Insufficient Data
    insufficient_data_message: getField('insufficient_data_message', 'yetersiz_veri_aciklamasi', coreMetrics) || null,
    is_insufficient_data: getField('insufficient_data', 'yetersiz_veri', validation) || false,
    recent_reels_count: getField('last_90d_reel_count', 'son_90_gun_reel_sayisi', validation) || 0,
    
    // Hidden Likes — v31: yalniz backend bayragi (sepet2<3 turetmesi kaldirildi)
    all_likes_hidden: backendAllLikesHidden,
    hidden_likes_count: coreMetrics.hidden_likes_count || 0,
    hidden_likes_error: coreMetrics.hidden_likes_error || null,

    // COMMIT-E1: per-metrik bos-sebep anahtarlari (D2 uretir) -> render sebep-metni
    visibility_reason_key: otherMetrics.visibility_reason_key || null,
    engagement_reason_key: otherMetrics.engagement_reason_key || null,
    forecast_views_reason_key: otherMetrics.forecast_views_reason_key || null,
    forecast_engagement_reason_key: otherMetrics.forecast_engagement_reason_key || null,
    
    // Status
    audit_durumu: getField('audit_status', 'audit_durumu', v4Result),
    
    // Activity Metrics
    velocity_score: getField('activity_score', 'reel_paylasma_aktifligi_kriter', otherMetrics) || 0,
    velocity_evaluation: getField('activity_status', 'aktiflik_durumu', otherMetrics) || '-',
    velocity_explanation: getField('activity_summary', 'aktiflik_son_X_gun_Y_reel', otherMetrics) || '-',
    velocity_user_explanation: getField('activity_description', 'aktiflik_aciklamasi', otherMetrics) || '-',
    activity: getField('activity_status', 'aktiflik_durumu', otherMetrics) || '-',
    
    // Visibility Performance
    visibility_performance: getField('visibility_score', 'gorunurluk_performansi_yuzde', otherMetrics) || null,
    visibility_evaluation: getField('visibility_rating', 'gorunurluk_performansi_degerlendirme', otherMetrics) || '-',
    visibility_color: getField('visibility_color', 'gorunurluk_performansi_renk', otherMetrics) || 'gray',
    visibility_avg_views: getField('avg_reel_views', 'gorunurluk_ortalama_izlenme', otherMetrics) || null,
    
    // Other metrics
    loyalty_rate: otherMetrics.sadakat_orani_puan || '-',
    engagement_quality: otherMetrics.etkilesim_kalitesi || '-',
    detected_brands: getField('detected_brands', 'tespit_edilen_markalar', otherMetrics) || [],
    
    // Brand partnerships
    paid_partnerships_count: getField('collaboration_count', 'ilk_12_reeldeki_is_birligi_sayisi', otherMetrics) || 0,
    brand_mentions: getField('detected_brands', 'tespit_edilen_markalar', otherMetrics) || [],
    collaborations_by_reel: getField('collaboration_reels', 'is_birligi_reelleri', otherMetrics) || [],
    
    // Collaboration details
    collab_details: otherMetrics.isbirligi_detaylari || [],
    
    // Validation Checks
    sepet_1_count: basket1Count,
    sepet_2_count: basket2Count,
    sepet_1_status: getField('basket_1_status', 'sepet_1_kont_sonucu', validation) || '-',
    sepet_2_status: getField('basket_2_status', 'sepet_2_kont_sonucu', validation) || '-',
    
    // Raw result for any additional data
    rawResult: v4Result
  };
};

/**
 * useAnalyzeReel - Ana analiz hook'u
 */
export const useAnalyzeReel = ({
  onAnalysisComplete,
  onRefreshHistory,
  talepKodu,
  talepKoduValidation,
  onFetchTalepKoduQueries,
  locale = 'tr',  // i18n: Language for backend evaluation labels
  currency  // secili gosterim para birimi -> backend user_inputs.currency (history dogru birim)
} = {}) => {
  const { user, token, refreshUserData, updateCredits } = useAuth();
  const { startTracking, stopTracking, activeAnalysis } = useAnalysisTracker();
  const { t } = useTranslation();
  
  // Form state
  const [username, setUsername] = useState("");
  const [paymentPerReel, setPaymentPerReel] = useState("");
  const [displayPayment, setDisplayPayment] = useState("");
  
  // Analysis state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [error, setError] = useState(null);
  
  // Quota state
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  
  // v15.0: Load persistent result from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(PERSISTENT_RESULT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // v32: HAM result sakla + restore'da GUNCEL mapper'dan gecir (stale-map onleme).
        // rawResult VARSA: her zaman bugunku mapV4Result ile map'le -> eski cache bile guncel mantikla map'lenir.
        if (parsed && parsed.timestamp && parsed.rawResult) {
          const ageHours = (Date.now() - parsed.timestamp) / (1000 * 60 * 60);
          if (ageHours < 24) {
            console.log('[useAnalyzeReel] Restoring persistent result (re-mapped from raw)');
            const remapped = mapV4Result(parsed.rawResult, parsed.username, t);
            setResult(remapped);
            setAnalysisComplete(true);
            // Also restore username for context
            if (parsed.username) {
              setUsername(parsed.username);
            }
          } else {
            // Stale data - clear it
            localStorage.removeItem(PERSISTENT_RESULT_KEY);
          }
        } else if (parsed && parsed.result) {
          // v32: ESKI mapped-only cache (pre-fix). Stale-map riski (orn. eminecnkl "Like'lar Gizli").
          // KULLANMA -> temizle; rapor DB'den taze map'lenerek gelir.
          localStorage.removeItem(PERSISTENT_RESULT_KEY);
        }
      }
    } catch (e) {
      console.error('[useAnalyzeReel] Failed to restore persistent result:', e);
      localStorage.removeItem(PERSISTENT_RESULT_KEY);
    }
  }, []);
  
  // v14.1 FIX: Listen to global tracker for completion
  // If tracker detects completion (via public endpoint), stop loading
  // v14.3: Also consume result from tracker and display on HomePage
  const trackerTaskIdRef = useRef(null);
  
  useEffect(() => {
    // v14.5: Fix - process result when tracker has completed analysis
    // Previously only worked when loading=true, missing page refresh scenarios
    if (!activeAnalysis) return;
    
    // Track the task_id to prevent duplicate processing.
    // FIX: terminal statüleri (completed + failed) guard'dan istisna tut — aksi halde
    // failed geçişinde erken-return setLoading(false)'u atlıyordu (spinner takılı kalıyordu).
    const TERMINAL_STATUSES = ['completed', 'failed'];
    if (trackerTaskIdRef.current === activeAnalysis.task_id && !TERMINAL_STATUSES.includes(activeAnalysis.status)) {
      return; // Already processing this task
    }
    
    if (activeAnalysis.status === 'completed') {
      console.log('[useAnalyzeReel] Tracker detected completion, processing result');
      
      // v14.3: Extract result from tracker and display on HomePage
      if (activeAnalysis.result) {
        try {
          // Result can be nested: result.result or directly result
          const v4Result = activeAnalysis.result.result || activeAnalysis.result;
          const mappedResult = mapV4Result(v4Result, activeAnalysis.username, t);
          
          setResult(mappedResult);
          setAnalysisComplete(true);
          setLoading(false);
          
          // v15.0: Save result to localStorage for persistence
          try {
            localStorage.setItem(PERSISTENT_RESULT_KEY, JSON.stringify({
              rawResult: v4Result,
              username: activeAnalysis.username,
              timestamp: Date.now()
            }));
            console.log('[useAnalyzeReel] Raw result saved to localStorage via tracker');
          } catch (e) {
            console.error('[useAnalyzeReel] Failed to save result to localStorage:', e);
          }
          
          // Mark as processed
          trackerTaskIdRef.current = activeAnalysis.task_id;
          
          // Refresh history
          if (onRefreshHistory) onRefreshHistory();
          if (onAnalysisComplete) onAnalysisComplete(mappedResult);
          
          // Stop tracking after consuming result
          stopTracking();
          
          console.log('[useAnalyzeReel] Result displayed on HomePage via tracker');
        } catch (e) {
          console.error('[useAnalyzeReel] Failed to map result from tracker:', e);
          setLoading(false);
        }
      } else {
        // Result not in tracker, just stop loading
        setLoading(false);
      }
    } else if (activeAnalysis.status === 'failed') {
      console.log('[useAnalyzeReel] Tracker detected failure');
      setLoading(false);
      setError(activeAnalysis.error || t('messages.analysis_failed'));
      trackerTaskIdRef.current = activeAnalysis.task_id;
      stopTracking();
    } else if (activeAnalysis.status === 'processing' && loading) {
      trackerTaskIdRef.current = activeAnalysis.task_id;
    }
  }, [activeAnalysis?.status, activeAnalysis?.result, activeAnalysis?.task_id, loading, onRefreshHistory, onAnalysisComplete, stopTracking]);
  
  // v14.2: Placeholder mesaj döngüsü kaldırıldı
  // Progress mesajları artık GlobalAnalysisProgress bileşeninde
  // backend'den gelen gerçek verilerle gösteriliyor

  /**
   * handlePaymentChange - Payment input formatting
   * Supports backspace, delete, and keyboard navigation
   */
  const handlePaymentChange = useCallback((e) => {
    const value = e.target.value;
    
    // Remove " TL" suffix and all non-digit characters
    const numericValue = value.replace(/[^\d]/g, '');
    
    // If empty or only had non-digits, clear the input
    if (numericValue === '') {
      setDisplayPayment('');
      setPaymentPerReel('');
      return;
    }
    
    const numberValue = parseInt(numericValue, 10);
    setPaymentPerReel(numberValue.toString());
    
    // Format with thousand separators (comma) only - currency shown separately
    const formatted = numberValue.toLocaleString('en-US');
    setDisplayPayment(formatted);
  }, []);

  /**
   * handlePaymentKeyDown - Handle backspace/delete to clear properly
   */
  const handlePaymentKeyDown = useCallback((e) => {
    const currentValue = e.target.value;
    const numericPart = currentValue.replace(/[^\d]/g, '');
    
    // For Backspace: manually handle deletion from the numeric part
    if (e.key === 'Backspace') {
      e.preventDefault(); // Prevent default backspace behavior
      
      if (numericPart.length <= 1) {
        // If only one digit or empty, clear everything
        setDisplayPayment('');
        setPaymentPerReel('');
      } else {
        // Remove last digit
        const newNumeric = numericPart.slice(0, -1);
        const numberValue = parseInt(newNumeric, 10);
        setPaymentPerReel(numberValue.toString());
        setDisplayPayment(numberValue.toLocaleString('en-US'));
      }
      return;
    }
    
    // For Delete key at the beginning
    if (e.key === 'Delete') {
      const selectionStart = e.target.selectionStart;
      
      if (selectionStart === 0 && numericPart.length > 0) {
        e.preventDefault();
        // Remove first digit
        const newNumeric = numericPart.slice(1);
        if (newNumeric.length === 0) {
          setDisplayPayment('');
          setPaymentPerReel('');
        } else {
          const numberValue = parseInt(newNumeric, 10);
          setPaymentPerReel(numberValue.toString());
          setDisplayPayment(numberValue.toLocaleString('en-US'));
        }
        return;
      }
    }
    
    // Allow: tab, escape, enter, arrows, home, end
    if (
      e.key === 'Tab' ||
      e.key === 'Escape' ||
      e.key === 'Enter' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'Home' ||
      e.key === 'End' ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))
    ) {
      return; // Allow these keys
    }
    
    // Block non-numeric input (but allow Delete for selections)
    if (e.key !== 'Delete' && !/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  }, [setDisplayPayment, setPaymentPerReel]);

  /**
   * resetForm - Formu sıfırla ve kalıcı raporu temizle
   * v15.0: "Yeni Analiz" butonuna basıldığında localStorage'ı da temizle
   */
  const resetForm = useCallback(() => {
    setUsername("");
    setPaymentPerReel("");
    setDisplayPayment("");
    setResult(null);
    setAnalysisComplete(false);
    setError(null);
    // v15.0: Clear persistent result from localStorage
    localStorage.removeItem(PERSISTENT_RESULT_KEY);
    console.log('[useAnalyzeReel] Form reset - persistent result cleared');
  }, []);

  /**
   * handleAnalyze - Ana analiz fonksiyonu
   */
  const handleAnalyze = useCallback(async () => {
    // Check if user is logged in
    if (!user || !token) {
      toast.error(t('notifications.login_required'), {
        duration: 3000
      });
      return;
    }
    
    // Check if user has credits
    if (user.credits_remaining <= 0 && !isPersonaStaff(user)) {
      setShowQuotaModal(true);
      return;
    }
    
    if (!username.trim()) {
      toast.error(t('notifications.username_required'));
      return;
    }

    setLoading(true);
    setResult(null);
    setAnalysisComplete(false);
    setError(null);

    // Save analysis state to localStorage for background processing
    const analysisId = `analysis_${username.trim()}_${Date.now()}`;
    localStorage.setItem('currentAnalysis', JSON.stringify({
      id: analysisId,
      username: username.trim(),
      startTime: Date.now(),
      status: 'running'
    }));

    try {
      // Build request with only required field (username)
      const requestData = {
        username: username.trim(),
        lang: locale,  // i18n: Send language for backend evaluation labels
        currency: currency  // secili gosterim para birimi (persist -> history dogru birim)
      };
      
      // Add payment only if provided
      if (paymentPerReel && parseFloat(paymentPerReel) > 0) {
        requestData.payment_per_reel = parseFloat(paymentPerReel);
      }
      
      // Add talep_kodu if validated and active (for team members)
      if (talepKoduValidation && talepKoduValidation.valid && talepKodu) {
        requestData.talep_kodu = talepKodu;
      }
      
      // FAZ 1: Use async queue endpoint
      const queueResponse = await axios.post(`${API}/analyze-v4-async`, requestData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // AUTHORITATIVE: submit yaniti decrement-SONRASI credits_remaining doner (server.py:16947);
      //   context'e ANINDA yaz -> ekranda hemen dususun (refreshUserData /auth/me yarisini beklemeden).
      if (queueResponse.data?.credits_remaining !== undefined) {
        updateCredits(queueResponse.data.credits_remaining);
      }

      // Task queued - start polling
      const taskId = queueResponse.data.task_id;
      const queuePosition = queueResponse.data.queue_position;
      
      // OPARA v12.0: Start global analysis tracking
      startTracking(taskId, username.trim(), locale);
      
      // v7.1: Error counter for network issues
      let pollErrorCount = 0;
      
      // Kuyrukta bekleniyorsa özel mesaj göster - artık sadece log
      if (queuePosition > 1) {
        console.log(`[useAnalyzeReel] Kuyrukta bekleniyor... (Sıra: ${queuePosition})`);
      }

      // Poll for task status
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await axios.get(`${API}/tasks/${taskId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          // Reset error count on successful poll
          pollErrorCount = 0;
          
          const taskStatus = statusResponse.data;
          
          // Kuyrukta bekleniyorsa sadece log - mesajlar GlobalAnalysisProgress'te
          if (taskStatus.status === 'queued' && taskStatus.queue_position > 1) {
            console.log(`[useAnalyzeReel] Kuyrukta bekleniyor... (Sıra: ${taskStatus.queue_position})`);
          }
          
          // Check if completed
          if (taskStatus.status === 'completed' && taskStatus.result) {
            clearInterval(pollInterval);
            
            // Process result
            const v4Result = taskStatus.result.result;
            const mappedResult = mapV4Result(v4Result, username.trim(), t);
            
            // Update localStorage
            localStorage.setItem('currentAnalysis', JSON.stringify({
              id: analysisId,
              username: username.trim(),
              startTime: Date.now(),
              status: 'completed',
              result: mappedResult
            }));
            
            // Refresh user data
            await refreshUserData();
            
            // OPARA v12.0: Stop global tracking (task completed locally)
            stopTracking();
            
            // Call callbacks
            if (onRefreshHistory) onRefreshHistory();
            if (talepKoduValidation && talepKoduValidation.valid && talepKodu && onFetchTalepKoduQueries) {
              onFetchTalepKoduQueries(talepKodu);
            }
            
            setTimeout(() => {
              setResult(mappedResult);
              setAnalysisComplete(true);
              setLoading(false);
              
              // v15.0: Save result to localStorage for persistence
              try {
                localStorage.setItem(PERSISTENT_RESULT_KEY, JSON.stringify({
                  rawResult: v4Result,
                  username: username.trim(),
                  timestamp: Date.now()
                }));
                console.log('[useAnalyzeReel] Raw result saved to localStorage for persistence');
              } catch (e) {
                console.error('[useAnalyzeReel] Failed to save result to localStorage:', e);
              }
              
              // v7.1: Show completion toast for 3 seconds
              toast.success(t('notifications.analysis_done'), {
                duration: 3000,
                style: {
                  background: '#10B981',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }
              });
              
              if (onAnalysisComplete) onAnalysisComplete(mappedResult);
            }, 500);
          } 
          // Check if failed
          else if (taskStatus.status === 'failed') {
            clearInterval(pollInterval);
            
            // COMMIT-C2b: YENI MODEL - basarisiz analiz yok. TUM terminal-fail'ler BIRLESIK mesaj:
            //   "sistem yogunlugundan tamamlanamadi, tamamlandiginda e-posta ile gonderilecek" (iade ibaresi YOK).
            //   wall_category/error_type backend'de persist KALIR (C3 staff triyaj); sadece MUSTERI mesaji birlesir.
            //   NOT: bu handler yalniz TERMINAL-FAIL'de calisir; "Cok Dusuk/Hesaplanamadi" (tamamlanmis verdict) buradan GECMEZ.
            // FAZ-B: PROFILE_NOT_FOUND ISTISNA -> hesap YOK, "e-posta gonderecegiz" YANLIS; net "bulunamadi" metni.
            const _isPnf = (taskStatus.wall_category === 'PROFILE_NOT_FOUND') || (taskStatus.error_type === 'PROFILE_NOT_FOUND');
            const userMessage = t(_isPnf ? 'errors.profile_not_found' : 'errors.analysis_rescue_pending');

            // v30.46: Kırmızı toast ile net uyarı (fail: kullanıcı okuyup kapatana kadar kalıcı)
            // FAZ-B madde-6: AnalysisTrackerContext.js:364 ile AYNI per-task id -> dedupe, tek toast kalir.
            toast.error(userMessage, { duration: Infinity, id: `analysis-failed-${taskId}` });

            // Loading state'i temizle — form tekrar kullanılabilir olsun
            setLoading(false);
            setError(userMessage);

            return;
          }
          // Queue position zaten yukarıda kontrol ediliyor
        } catch (pollError) {
          // v7.1: Track consecutive errors
          pollErrorCount++;
          console.error(`Poll error (${pollErrorCount}/5):`, pollError);
          
          // After 5 consecutive errors, do a final check and show guidance
          if (pollErrorCount >= 5) {
            clearInterval(pollInterval);
            toast.info(t('notifications.connection_issue'), {
              duration: 5000
            });
          }
        }
      }, 3000); // Poll every 3 seconds

      // Timeout after 5 minutes - but check task one final time
      setTimeout(async () => {
        clearInterval(pollInterval);
        if (loading) {
          // v7.1: Final check before timeout - maybe task completed but we missed it
          try {
            const finalCheck = await axios.get(`${API}/tasks/${taskId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const finalStatus = finalCheck.data;
            
            if (finalStatus.status === 'completed' && finalStatus.result) {
              // Task actually completed! Show results
              const v4Result = finalStatus.result.result;
              const mappedResult = mapV4Result(v4Result, username.trim(), t);
              
              setResult(mappedResult);
              setAnalysisComplete(true);
              setLoading(false);
              
              // v15.0: Save result to localStorage for persistence
              try {
                localStorage.setItem(PERSISTENT_RESULT_KEY, JSON.stringify({
                  rawResult: v4Result,
                  username: username.trim(),
                  timestamp: Date.now()
                }));
                console.log('[useAnalyzeReel] Raw result saved to localStorage via timeout check');
              } catch (e) {
                console.error('[useAnalyzeReel] Failed to save result to localStorage:', e);
              }
              
              toast.success(t('messages.analysis_complete'), {
                duration: 3000,
                style: {
                  background: '#10B981',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }
              });
              
              if (onRefreshHistory) onRefreshHistory();
              if (onAnalysisComplete) onAnalysisComplete(mappedResult);
              return;
            }
          } catch (e) {
            console.error("Final check failed:", e);
          }
          
          // Still not completed - show timeout message with retry hint
          setLoading(false);
          setError(t('messages.analysis_timed_out'));
          toast.error(t('notifications.analysis_timeout_body'), {
            duration: 5000
          });
        }
      }, 300000); // 5 minutes

    } catch (error) {
      console.error("Error:", error);
      console.error("Error response:", error.response);
      
      // Update localStorage with error status
      localStorage.setItem('currentAnalysis', JSON.stringify({
        id: analysisId,
        username: username.trim(),
        startTime: Date.now(),
        status: 'error',
        error: error.response?.data?.detail || error.message
      }));
      
      // Get user-friendly error message
      const userMessage = getReadableErrorMessage(error, t);
      setError(userMessage);
      
      // Handle different error types with smart routing
      if (error.response?.status === 401) {
        toast.error(userMessage);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/giris";
      } else if (error.response?.status === 403 || error.response?.data?.detail === 'insufficient_credits') {
        // Analiz hakkı/limit hatası - refresh user data ve kalıcı toast
        toast.error(userMessage, {
          duration: Infinity,
          action: {
            label: t('notifications.buy_package'),
            onClick: () => window.location.href = '/paketler'
          }
        });
        await refreshUserData();
      } else {
        // Diğer tüm hatalar
        toast.error(userMessage, {
          duration: 8000
        });
      }
      
      setTimeout(() => {
        localStorage.removeItem('currentAnalysis');
      }, 5000);
      
      setLoading(false);
    }
  }, [user, token, username, paymentPerReel, talepKodu, talepKoduValidation, refreshUserData, updateCredits, onRefreshHistory, onFetchTalepKoduQueries, onAnalysisComplete, loading]);

  return {
    // Form state
    username,
    setUsername,
    paymentPerReel,
    setPaymentPerReel,
    displayPayment,
    setDisplayPayment,
    handlePaymentChange,
    handlePaymentKeyDown,
    
    // Analysis state
    loading,
    setLoading,
    result,
    setResult,
    analysisComplete,
    setAnalysisComplete,
    error,
    
    // Quota
    showQuotaModal,
    setShowQuotaModal,
    
    // Actions
    handleAnalyze,
    resetForm
  };
};

export default useAnalyzeReel;
